import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";
import { getColor } from "../../brandConfig";

/**
 * Meet Module Config
 *
 * SINGLE SOURCE OF TRUTH for permissions is the backend (meet/permissions.ts),
 * fetched via `modulePermissions`. Routes/nav use `permissionLevel` resolved at runtime.
 *
 * Routes:
 *   /meet              host surface (authed) — plan a meeting, copy the link to send
 *   /meet/speakers     host surface (authed) — name the voices Meet has heard
 *   /meet/:room/manage host surface (authed) — who is coming, what you sent, the recordings
 *   /meet/:room        PUBLIC join page — guests open the link and walk in, no login
 *
 * POLISH v1 2026-10-17: every page composes the module's own kit (`components/ui/`) and the three
 * host pages hide, never disable, anything the viewer's permissions would refuse. See CLAUDE.md.
 */

const MeetList = React.lazy(() => import("./MeetList"));
const MeetRoom = React.lazy(() => import("./MeetRoom"));
const MeetDetail = React.lazy(() => import("./MeetDetail"));
const MeetSpeakers = React.lazy(() => import("./MeetSpeakers"));

const meetModuleConfig: ModuleConfig = {
  id: "meet",
  name: "Meet",
  description: "Video meetings on your own site. Send a link and they are in — camera, microphone and screen-sharing in the browser, nothing to install. Every meeting is recorded and written up for you.",
  version: "1.1.0",
  enabled: true,
  category: "communication",
  order: 11,
  icon: "🎥",
  color: getColor("primaryBlue"),

  navigation: [
    {
      label: "Meet",
      path: "/meet",
      icon: "🎥",
      permissionLevel: "view",
    },
    {
      label: "Meet Speakers",
      path: "/meet/speakers",
      icon: "🎙️",
      permissionLevel: "view",
    },
  ],

  // Permissions resolved from backend at runtime via permissionLevel.
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  routes: [
    {
      path: "/meet",
      component: MeetList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      // AUTHED — Meet Speakers voice-signature gallery. MUST come before the
      // public /meet/:room catch-all so "speakers" isn't treated as a room slug.
      path: "/meet/speakers",
      component: MeetSpeakers,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      // AUTHED — host management page (details, participants, recordings, send).
      path: "/meet/:room/manage",
      component: MeetDetail,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      // PUBLIC — guests join via the shared link with no auth.
      path: "/meet/:room",
      component: MeetRoom,
      public: true,
      useLayout: false,
    },
  ],
};

export default meetModuleConfig;
